import React, {FunctionComponent} from 'react';
import {useTranslation} from 'react-i18next';
import styles from './invitation-progress.module.scss';
import {Invitation} from '../../../types/invitation';
import {InvitationProgress} from '../../../types/invitation-progress';
import {buildProgressSteps, StepState, summarize} from '../../../utils/invitation-progress';

interface Props {
    invitation: Invitation;
    progress?: InvitationProgress;
    failed: boolean;
}

const STATE_LABELS: { [state in StepState]: string } = {
    [StepState.Done]: 'Done',
    [StepState.Current]: 'In progress',
    [StepState.Attention]: 'Needs attention',
    [StepState.Upcoming]: 'Not started',
    [StepState.Skipped]: 'Not applicable',
};

const InvitationProgressComponent: FunctionComponent<Props> = ({invitation, progress, failed}: Props) => {
    const {t} = useTranslation();

    if (failed) {
        return (
            <section className={styles.panel}>
                <h2 className={styles.panelTitle}>{t('Progress')}</h2>
                <p className={styles.error}>
                    {t('The progress could not be loaded. Refresh the page to try again.')}
                    {' '}{t('Invitation status')}: <code className={styles.code}>{invitation.status}</code>
                </p>
            </section>
        );
    }

    if (!progress) {
        return (
            <section className={styles.panel} aria-busy="true">
                <h2 className={styles.panelTitle}>{t('Progress')}</h2>
                <div className={styles.skeleton}>
                    {[0, 1, 2, 3, 4].map((row) => <div key={row} className={styles.skeletonRow}/>)}
                </div>
            </section>
        );
    }

    const steps = buildProgressSteps(invitation, progress, t);
    const summary = summarize(steps, t);

    return (
        <section className={styles.panel}>
            <h2 className={styles.panelTitle}>{t('Progress')}</h2>
            <div className={`${styles.summary} ${styles[summary.state]}`} role="status">
                <div className={styles.summaryHeadline}>{summary.headline}</div>
                {summary.text && <div className={styles.summaryText}>{summary.text}</div>}
            </div>
            <ol className={styles.steps}>
                {steps.map((step) =>
                    <li key={step.key} className={`${styles.step} ${styles[step.state]}`}>
                        <span className={styles.marker} aria-hidden="true"/>
                        <div className={styles.body}>
                            <div className={styles.titleRow}>
                                <span className={styles.title}>{step.title}</span>
                                <span className={styles.visuallyHidden}>({t(STATE_LABELS[step.state])})</span>
                                {step.code && <code className={styles.code} title={t('Status as stored in the system')}>{step.code}</code>}
                            </div>
                            <p className={styles.description}>{step.description}</p>
                            {step.detail &&
                                <details className={styles.detail}>
                                    <summary>{t('Technical details')}</summary>
                                    <pre>{step.detail}</pre>
                                </details>}
                        </div>
                    </li>)}
            </ol>
        </section>
    );
};

export default InvitationProgressComponent;
